import { useState } from "react";
import { Star, X, Heart } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * RatingModal — customer rates the driver after delivery.
 * 1-5 stars (required) + optional tip bonus + optional comment.
 * On submit, calls POST /orders/{id}/rate.
 *
 * Props:
 *   orderId, driverName, onSubmitted() — called on success, onClose()
 */
export default function RatingModal({ orderId, driverName = "your driver", onSubmitted, onClose }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [tip, setTip] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!rating) {
      toast.error("Please pick a star rating first.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/rate`, {
        rating,
        tip_bonus: tip,
        comment: comment.trim(),
      });
      toast.success(tip > 0 ? `Thanks! $${tip.toFixed(2)} bonus tip sent to ${driverName}.` : "Thanks for rating your delivery!");
      onSubmitted?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const active = hover || rating;
  const chips = [0, 2, 5, 10];

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-3 sm:p-6 fade-up"
      data-testid="rating-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-[28px] shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="p-5 flex items-center gap-3 border-b border-neutral-100">
          <div className="w-11 h-11 rounded-2xl bg-eatup-green/15 text-eatup-green flex items-center justify-center">
            <Heart className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.35em] font-black text-eatup-green">Delivered</div>
            <div className="font-display font-bold text-lg text-eatup-charcoal">How was {driverName}?</div>
          </div>
          <button
            onClick={onClose}
            data-testid="rating-close-btn"
            className="w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center transition"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Stars */}
          <div className="flex items-center justify-center gap-2" onMouseLeave={() => setHover(0)}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onMouseEnter={() => setHover(n)}
                onClick={() => setRating(n)}
                data-testid={`rating-star-${n}`}
                className="p-1 transition transform hover:scale-110 active:scale-95"
                aria-label={`${n} stars`}
              >
                <Star
                  className={`w-10 h-10 transition ${
                    n <= active ? "text-eatup-yellow fill-eatup-yellow drop-shadow-[0_0_10px_rgba(240,196,25,0.4)]" : "text-neutral-300"
                  }`}
                  strokeWidth={2}
                />
              </button>
            ))}
          </div>
          <div className="text-center text-xs font-semibold text-neutral-500 -mt-2">
            {active === 0 && "Tap a star"}
            {active === 1 && "Not great"}
            {active === 2 && "Could be better"}
            {active === 3 && "Alright"}
            {active === 4 && "Nice trip"}
            {active === 5 && "Amazing — send a bonus?"}
          </div>

          {/* Optional tip bonus */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-2">Add a bonus tip (optional)</div>
            <div className="flex items-center gap-2 flex-wrap">
              {chips.map((amount) => (
                <button
                  key={amount}
                  onClick={() => setTip(amount)}
                  data-testid={`tip-chip-${amount}`}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
                    tip === amount
                      ? "bg-eatup-green border-eatup-green text-white"
                      : "bg-white border-neutral-200 text-eatup-charcoal hover:border-eatup-green/50"
                  }`}
                >
                  {amount === 0 ? "No thanks" : `+ $${amount}`}
                </button>
              ))}
              <div className="flex-1 min-w-[100px]">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-400 font-semibold">$</span>
                  <input
                    data-testid="tip-custom"
                    type="number"
                    min="0"
                    step="0.5"
                    inputMode="decimal"
                    placeholder="Custom"
                    value={tip && ![0, 2, 5, 10].includes(tip) ? tip : ""}
                    onChange={(e) => setTip(Math.max(0, parseFloat(e.target.value) || 0))}
                    className="w-full pl-6 pr-3 py-2 rounded-full border border-neutral-200 outline-none focus:border-eatup-green text-sm font-semibold tabular"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Optional comment */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-2">Add a note (optional)</div>
            <textarea
              data-testid="rating-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
              placeholder="Tell us what stood out…"
              className="w-full px-4 py-3 rounded-2xl border border-neutral-200 outline-none focus:border-eatup-green text-sm resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-neutral-100 flex items-center gap-3">
          <button
            onClick={onClose}
            data-testid="rating-skip-btn"
            className="flex-1 py-3 rounded-full bg-neutral-100 hover:bg-neutral-200 text-eatup-charcoal font-semibold text-sm transition"
          >
            Later
          </button>
          <button
            onClick={submit}
            disabled={busy || rating === 0}
            data-testid="rating-submit-btn"
            className={`flex-1 py-3 rounded-full font-semibold text-sm transition ${
              busy || rating === 0
                ? "bg-eatup-green/40 text-white/70 cursor-not-allowed"
                : "bg-eatup-green hover:bg-eatup-green-dark text-white active:scale-[0.98]"
            }`}
          >
            {busy ? "Sending…" : tip > 0 ? `Submit + $${tip.toFixed(2)} tip` : "Submit rating"}
          </button>
        </div>
      </div>
    </div>
  );
}
